import React, { useContext, useEffect, useState } from "react";
import PatientDetails from "../../Components/PatientInformation";
import ErrorField from "../../Components/ErrorField";
import {
  AddLocalStorageData,
  DateInRange,
  GetLocalStorageData,
  GetLoggedInUserID,
  IsAdmin,
  IsLessOrEqual,
  IsValidDateValue,
  IsDateChainValid,
  useCollapsibleGroups,
  CollapsibleSectionHeader,
} from "../../Helpers/Utilities";
import { Collapse } from "react-bootstrap";
import {
  SideMenuItems,
  HTTPResponse,
  ToastMessageType,
  ToastMessages,
  RRMMCohortTreatment,
} from "../../Helpers/Enums";
import {
  CommonError_Msgs,
  EligibilityCriteria_Msgs,
} from "../../Helpers/HelperText";
import { AppContext } from "../../Contexts/AppContextProvider";
import DatePickerComponent from "../../Components/DatePickerComponent";
import dayjs from "dayjs";
import SaveAndNavigate from "../../Components/SaveAndNavigate";
import SideBarMenu from "../../Components/SideBarMenu";
import { NDMMTreatmentCohortService } from "../../WebApiServices/NDMMTreatmentCohortService.service";
import { BispecificsCartTreatmentCohortService } from "../../WebApiServices/BispecificsCartTreatmentCohort.service";

// AG11.1: a cohort's own bispecific must not be offered back as its combination agent -
// hide Teclistamab from Talquetamab's list (BCMA targeted therapy) and Talquetamab from
// Teclistamab's list (GPRC5D targeted therapy); GPRC5D has no other agent, so hide its
// category header too once Talquetamab is removed from it.
const getExcludedOwnBispecificAgentName = (cohortTreatmentID) =>
  cohortTreatmentID === RRMMCohortTreatment.TALVEY
    ? "Teclistamab"
    : cohortTreatmentID === RRMMCohortTreatment.TECVAYLI
      ? "Talquetamab"
      : null;

const excludeOwnBispecificAgent = (agents, cohortTreatmentID) => {
  const excludedName = getExcludedOwnBispecificAgentName(cohortTreatmentID);

  if (!excludedName) return agents;

  return agents.filter((a) => !a.agentName?.startsWith(excludedName));
};

// Blocks typing the hidden agent's name into the "Other, please specify" free-text field -
// otherwise a user could re-enter the exact option that was removed from the dropdown.
const isExcludedOwnBispecificAgentTyped = (otherValue, cohortTreatmentID) => {
  const excludedName = getExcludedOwnBispecificAgentName(cohortTreatmentID);
  return (
    !!excludedName &&
    !!otherValue?.trim() &&
    otherValue.trim().toLowerCase().startsWith(excludedName.toLowerCase())
  );
};

const groupAndSortByCategory = (agents) => {
  const grouped = agents.reduce((acc, item) => {
    const category = item.agentCategory || "Other";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  Object.keys(grouped).forEach((category) => {
    grouped[category].sort((a, b) => a.sortOrder - b.sortOrder);
  });

  const sortedEntries = Object.entries(grouped).sort(
    ([, agentsA], [, agentsB]) => {
      const minSortOrderA = Math.min(...agentsA.map((a) => a.sortOrder));
      const minSortOrderB = Math.min(...agentsB.map((a) => a.sortOrder));
      return minSortOrderA - minSortOrderB;
    },
  );

  return Object.fromEntries(sortedEntries);
};

const sortAgents = (agents) => {
  return agents.sort((a, b) => a.sortOrder - b.sortOrder);
};

const allRowsHaveAgents = (agentRows) => {
  return agentRows.every((row) => row.agentid !== null && row.agentid !== "");
};

const isOtherAgentSelected = (agentId, agentList) => {
  if (!agentId || !agentList) return false;

  const selectedAgent = agentList.find((agent) => agent.agentid === agentId);
  if (!selectedAgent) return false;

  return selectedAgent.agentName?.toLowerCase().includes("other");
};

// earliest of all known initiation dates for this treatment
const getEarliestTreatmentInitiationDate = (treatment) => {
  const knownDates = (treatment.agentRows || [])
    .filter(
      (row) =>
        row.initiationDateRadio === 1 &&
        row.initiationDate &&
        String(row.initiationDate).trim() !== "",
    )
    .map((row) => dayjs(row.initiationDate));

  if (knownDates.length === 0) return null;

  return knownDates.reduce((min, d) => (d.isBefore(min) ? d : min));
};

// saved agents are keyed by cohort and line of therapy (no lot = 0)
const getSavedDataKey = (cohortId, lot) => `${cohortId}_${lot || 0}`;

// AG11.1 date rules for every cohort/lot group. Returns, per group:
//  unknownDate    - (1) an agent has an "Unknown" initiation date -> cohort becomes ineligible
//  dateOrderError - (2) DOB <= MM diagnosis <= NDMM index date < 2L index date < 3L index date < ...
//                   (lots are compared across all cohorts, in ascending lot order)
//  ageError       - (3) year of index date - year of birth < 18
const evaluateCohortDateRules = (treatments, eligibilityDates) => {
  const parse = (d) => (IsValidDateValue(d) ? dayjs(d) : null);
  const dob = parse(eligibilityDates?.dateOfBirth);
  const diagnosis = parse(eligibilityDates?.initialMMDiagnosisDate);
  const ndmm = parse(eligibilityDates?.ndmmIndexDate);

  const groups = (treatments || []).map((t) => ({
    cohortId: t.cohortTreatmentID,
    lot: t.lineOfTherapy > 0 ? t.lineOfTherapy : null,
    indexDate: parse(t.computedIndexDate),
  }));

  return (treatments || []).map((treatment, idx) => {
    const { lot, indexDate } = groups[idx];

    const unknownDate = (treatment.agentRows || []).some(
      (r) => r.initiationDateRadio === 99,
    );

    let dateOrderError = false;
    let ageError = false;

    if (indexDate) {
      // DOB <= diagnosis <= NDMM index date (dates not reported are skipped) - same
      // non-decreasing chain check used elsewhere in this form
      if (!IsDateChainValid(dob, diagnosis, ndmm, indexDate)) {
        dateOrderError = true;
      }

      // NDMM index date < this lot's index date - IsDateChainValid only checks <=, so the
      // strict part (only required once the group has a lot) is a separate explicit check
      const lastKnownPriorDate = [dob, diagnosis, ndmm].filter(Boolean).pop();
      if (
        lot !== null &&
        lastKnownPriorDate &&
        !lastKnownPriorDate.isBefore(indexDate, "day")
      ) {
        dateOrderError = true;
      }

      // across all cohorts (no two cohorts share a lot), index dates must be strictly
      // increasing with the lot number. Only the nearest dated lower / higher lot is
      // compared, so just the groups that actually break the chain are flagged.
      if (lot !== null) {
        const dated = groups.filter(
          (g, gIdx) => gIdx !== idx && g.indexDate && g.lot !== null && g.lot !== lot,
        );
        const prev = dated
          .filter((g) => g.lot < lot)
          .sort((a, b) => b.lot - a.lot)[0];
        const next = dated
          .filter((g) => g.lot > lot)
          .sort((a, b) => a.lot - b.lot)[0];
        if (prev && !prev.indexDate.isBefore(indexDate, "day"))
          dateOrderError = true;
        if (next && !indexDate.isBefore(next.indexDate, "day"))
          dateOrderError = true;
      }

      if (dob && indexDate.year() - dob.year() < 18) {
        ageError = true;
      }
    }

    return { unknownDate, dateOrderError, ageError };
  });
};

// Index date = earliest of (cohortIndexDate, earliestInitiationDate)
const computeIndexDate = (treatment) => {
  const baseIndexDate =
    treatment?.cohortIndexDate &&
    String(treatment?.cohortIndexDate).trim() !== ""
      ? dayjs(treatment?.cohortIndexDate)
      : null;

  const earliestInitiationDate = getEarliestTreatmentInitiationDate(treatment);

  if (baseIndexDate && earliestInitiationDate) {
    const earliest = earliestInitiationDate.isBefore(baseIndexDate)
      ? earliestInitiationDate
      : baseIndexDate;
    return earliest.format("MM/DD/YYYY");
  }

  if (baseIndexDate) return baseIndexDate.format("MM/DD/YYYY");
  if (earliestInitiationDate)
    return earliestInitiationDate.format("MM/DD/YYYY");
  return "";
};

const BispecificInitiation = () => {
  const {
    ShowToast,
    ToggleLoader,
    HandleSessionTimeout,
    isSideMenuActive,
    setIsSideMenuActive,
    patientConfigData,
    ShowActionPopUp,
  } = useContext(AppContext);

  const [showTopErrMsg, setShowTopErrMsg] = useState(false);
  const [agentList, setAgentList] = useState([]);
  const [terminate, setTerminate] = useState(false);
  const [treatmentErrors, setTreatmentErrors] = useState({});
  // per‑cohort soft terminate flags (for warning + termination on save)
  const [cohortTerminate, setCohortTerminate] = useState({});
  // per-group date order / age rule failures (AG11.1 rules 2 and 3), these block validating the form
  const [cohortDateErrors, setCohortDateErrors] = useState({});
  // expand/collapse state of each cohort group, keyed by index
  const { isCollapsed: isCohortCollapsed, toggleGroup: toggleCohortCollapse } =
    useCollapsibleGroups();

  // Eligibility states (like RRMM)
  const [isEligible, setIsEligible] = useState(true);
  const [isAllDataFilled, setIsAllDataFilled] = useState(false);
  const [eligibilityCriteriaLocked, setEligibilityCriteriaLocked] =
    useState(false);

  // dates from eligibility model
  const [eligibilityDates, setEligibilityDates] = useState({
    dateOfBirth: null,
    ndmmIndexDate: null, // InitiationDate1LTreatment
    initialMMDiagnosisDate: null,
  });

  // global eligibility warning flag
  const [eligibilityWarning, setEligibilityWarning] = useState(false);

  const rowTemplate = {
    rowId: 1,
    agentid: null,
    agentOtherValue: "",
    initiationDate: "",
    initiationDateRadio: null,
    agentError: false,
    agentOtherExcludedError: false,
    dateError: false,
    invalidDateError: false,
  };

  const [bispecificTreatments, setBispecificTreatments] = useState([]);
  const pid = GetLocalStorageData("patientId") ?? 0;

  const terminatedPageId = patientConfigData?.data?.terminatedPageId;

  //Additional Exclusion Criteria is skipped entirely when it has nothing to ask
  //(no NDMM cohort and no RRMM cohort with a displayable lot).
  const afterComboTherapyPage =
    patientConfigData?.data?.showExclusionCriteriaPage === false
      ? "/TransitionToChartAbstraction"
      : "/ExclusionCriteria";

  const navigatePaths = {
    prevPage: "/RRMMCriteria",
    currPage: "/RRMMComboTherapy",
    nextPage:
      terminatedPageId === SideMenuItems.RRMMComboTherapy
        ? "/ManagePatients"
        : (isAllDataFilled || eligibilityCriteriaLocked) && isEligible
          ? afterComboTherapyPage
          : isAllDataFilled && !isEligible
            ? "/ManagePatients"
            : "/RRMMComboTherapy",
  };

  useEffect(() => {
    async function FetchData() {
      if (!isNaN(pid) && Number(pid) !== 0) {
        ToggleLoader(true);
        await LoadData();
        AddLocalStorageData("loadConfig", true);
        ToggleLoader(false);
      }
    }
    FetchData();
  }, []);

  const LoadData = async () => {
    try {
      if (pid > 0) {
        const response = await NDMMTreatmentCohortService.GetTableData(pid);

        if (response?.status === HTTPResponse.OK) {
          if (
            response.data?.q24_agentLists &&
            response.data.q24_agentLists.length > 0
          ) {
            setAgentList(response.data.q24_agentLists);
          }

          const storedData = GetLocalStorageData("CohortTreatment");
          if (storedData) {
            let cohortTreatmentList = JSON.parse(storedData);

            const isAdmin = false;
            let eligibilityDates = {};

            const savedDataResponse =
              await BispecificsCartTreatmentCohortService.GetBispecificInitiationData(
                pid,
                isAdmin,
              );
            let savedData = {};
            let hasSavedBispecificData = false;
            let bispecificDetails = [];
            if (savedDataResponse?.status === HTTPResponse.OK) {
              const responseData = savedDataResponse.data;

              setEligibilityCriteriaLocked(
                responseData?.eligibilityLocked ?? false,
              );
              if (responseData.eligibleCohortExists) {
                setTerminate(
                  responseData.eligibleCohortExists === true ? false : true,
                );
              }

              eligibilityDates = {
                dateOfBirth:
                  responseData?.dateOfBirth ||
                  responseData?.DateOfBirth ||
                  null,
                ndmmIndexDate:
                  responseData?.initiationDate1LTreatment ||
                  responseData?.InitiationDate1LTreatment ||
                  null,
                initialMMDiagnosisDate:
                  responseData?.initialMMDiagnosisDate ||
                  responseData?.InitialMMDiagnosisDate ||
                  null,
              };
              setEligibilityDates(eligibilityDates);

              bispecificDetails = responseData?.bispecificDetails || [];

              if (
                bispecificDetails &&
                Array.isArray(bispecificDetails) &&
                bispecificDetails.length > 0
              ) {
                hasSavedBispecificData = bispecificDetails.some((detail) => {
                  const agents =
                    detail.agents || detail.Agents || detail.agentRows || [];
                  return agents.length > 0;
                });

                savedData = bispecificDetails.reduce((acc, treatment) => {
                  const cohortId =
                    treatment.cohortTreatmentID ||
                    treatment.CohortTreatmentID ||
                    treatment.cohorttreatmentid;

                  const agents =
                    treatment.agents ||
                    treatment.Agents ||
                    treatment.agentRows ||
                    [];

                  if (cohortId) {
                    acc[
                      getSavedDataKey(
                        cohortId,
                        treatment.lotnumber ?? treatment.Lotnumber,
                      )
                    ] = agents;
                  }
                  return acc;
                }, {});
              }
            }

            if (hasSavedBispecificData) {
              // Show all RRMM cohorts (ndmm=false) when there's saved data
              cohortTreatmentList = cohortTreatmentList.filter(
                (c) => c.ndmm !== true && c.monoTherapyReceived === false,
              );

              // Get cohort IDs that are in savedData
              const savedCohortIds = Object.keys(savedData).map((key) =>
                parseInt(key.split("_")[0]),
              );

              // Also check the FRESH backend response, not just the cached list - the backend
              // itself now fills a cohort in from Rrmmtreatmentreceived when it's missing its
              // Patientcohorttreatmentbridge row, so it may already be complete here even
              // though the stale cache doesn't have it yet. Rebuilding it again below would
              // duplicate every one of its lots.
              const existingCohortIds = cohortTreatmentList
                .map((c) => c.cohortTreatmentID)
                .concat(
                  (savedDataResponse?.data?.cohortTreatmentModelList || []).map(
                    (c) => c.cohortTreatmentID,
                  ),
                );

              // Find missing cohort IDs (have saved data but not in current list)
              const missingCohortIds = savedCohortIds.filter(
                (id) => !existingCohortIds.includes(id),
              );

              // ✅ For missing cohorts, build from bispecificDetails
              if (missingCohortIds.length > 0 && bispecificDetails) {
                bispecificDetails.forEach((detail) => {
                  const cohortId =
                    detail.cohortTreatmentID ||
                    detail.CohortTreatmentID ||
                    detail.cohorttreatmentid;

                  if (
                    missingCohortIds.includes(cohortId) &&
                    cohortId >= 9 &&
                    cohortId <= 14
                  ) {
                    //  Build cohort object from bispecificDetails
                    // This cohort has no Patientcohorttreatmentbridge row yet (e.g. RRMM Criteria
                    // hasn't been saved this round), so CommonService never returns it and there is
                    // no ReceivedLinesOfTherapy to draw lots from - use this detail's own lot
                    // instead. Each lot for the same missing cohort produces its own detail entry
                    // here, one push per lot.
                    cohortTreatmentList.push({
                      cohortTreatmentID: cohortId,
                      cohortTreatmentName:
                        detail.cohortTreatmentName ||
                        detail.CohortTreatmentName ||
                        getCohortNameById(cohortId), // fallback to static mapping
                      cohortTreatmentNameSuperscript:
                        detail.cohortTreatmentNameSuperscript ||
                        detail.CohortTreatmentNameSuperscript ||
                        "",
                      cohortIndexDate:
                        detail.cohortIndexDate || detail.CohortIndexDate || "",
                      lineOfTherapy:
                        detail.lotnumber || detail.Lotnumber || null,
                      isEligible: false,
                      ndmm: false,
                      monoTherapyReceived: true,
                    });
                  }
                });
              }
            } else {
              // Show only combination cohorts when no saved data
              cohortTreatmentList = cohortTreatmentList.filter(
                (c) => c.ndmm !== true && c.monoTherapyReceived == false,
              );
            }

            // Dedup by cohort + lot, not cohort alone - a cohort missing its
            // Patientcohorttreatmentbridge row can have several placeholder
            // entries above (one per lot), all of which need to survive here.
            const uniqueCohortList = cohortTreatmentList.reduce(
              (acc, current) => {
                const exists = acc.find(
                  (item) =>
                    item.cohortTreatmentID === current.cohortTreatmentID &&
                    (item.lineOfTherapy ?? null) ===
                      (current.lineOfTherapy ?? null),
                );
                if (!exists) {
                  acc.push(current);
                }
                return acc;
              },
              [],
            );

            // One group per cohort per line of therapy (Rrmmtreatmentreceived);
            // prefer the fresh server value over the cached list
            const initialData = uniqueCohortList.flatMap((treatment) => {
              const receivedLots =
                (savedDataResponse?.data?.cohortTreatmentModelList || []).find(
                  (c) => c.cohortTreatmentID === treatment.cohortTreatmentID,
                )?.receivedLinesOfTherapy ??
                treatment.receivedLinesOfTherapy ??
                // Placeholder cohort (see above) - it already represents exactly one lot
                (treatment.lineOfTherapy
                  ? [
                      {
                        lineOfTherapy: treatment.lineOfTherapy,
                        indexDate: treatment.cohortIndexDate,
                      },
                    ]
                  : []);
              // A cohort is listed when ANY of its lots is combination, but only the
              // combination lots belong on this page - drop the mono therapy lots
              const comboLots = receivedLots.filter(
                (l) => l.monoTherapyReceived !== true,
              );
              if (receivedLots.length > 0 && comboLots.length === 0) return [];
              const lots = comboLots.length > 0 ? comboLots : [null];

              return lots.map((lotInfo, lotIdx) => {
                const lot = lotInfo?.lineOfTherapy ?? null;
                // rows saved before lots were tracked have no lot number, show them under the first lot
                const savedAgents =
                  savedData[
                    getSavedDataKey(treatment.cohortTreatmentID, lot)
                  ] ||
                  (lotIdx === 0
                    ? savedData[
                        getSavedDataKey(treatment.cohortTreatmentID, null)
                      ]
                    : null) ||
                  [];

                const baseTreatment = {
                  cohortTreatmentID: treatment.cohortTreatmentID,
                  cohortTreatmentName: treatment.cohortTreatmentName,
                  superScriptText: treatment.cohortTreatmentNameSuperscript,
                  // each lot has its own index date (Rrmmtreatmentreceived)
                  cohortIndexDate: lotInfo
                    ? lotInfo.indexDate || ""
                    : treatment.cohortIndexDate,
                  lineOfTherapy: lot,
                  agentRows:
                    savedAgents.length > 0
                      ? savedAgents.map((agent, idx) => {
                          return {
                            rowId: idx + 1,
                            agentid: agent.agentid || agent.Agentid,
                            agentOtherValue:
                              agent.agentOtherValue ||
                              agent.AgentOtherValue ||
                              "",
                            initiationDate:
                              agent.initiationDate ||
                              agent.InitiationDate ||
                              "",
                            initiationDateRadio:
                              agent.initiationDateRadio ||
                              agent.InitiationDateRadio,
                            agentError: false,
                            agentOtherExcludedError: false,
                            dateError: false,
                            invalidDateError: false,
                            rrmmtreatmentagentbridgeid:
                              agent.rrmmtreatmentagentbridgeid ||
                              agent.Rrmmtreatmentagentbridgeid,
                          };
                        })
                      : [],
                };

                return {
                  ...baseTreatment,
                  computedIndexDate: computeIndexDate(baseTreatment),
                };
              });
            });

            setBispecificTreatments(initialData);
            // After loading, recompute terminate flags for all cohorts
            initialData.forEach((itm, idx) => {
              recomputeTerminateForTreatment(
                idx,
                itm,
                eligibilityDates,
                true,
                initialData,
              );
            });

            if (savedDataResponse.data.cohortTreatmentModelList.length > 0)
              AddLocalStorageData(
                "CohortTreatment",
                JSON.stringify(savedDataResponse.data.cohortTreatmentModelList),
              );
            // else AddLocalStorageData("CohortTreatment", JSON.stringify([]));
          }
        } else if (response?.status === HTTPResponse.Unauthorized) {
          HandleSessionTimeout();
        } else {
          throw response?.error;
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
      ShowToast(CommonError_Msgs.LOAD_ERR, ToastMessageType.Failed);
    }
  };

  const recalcComputedIndexForTreatment = (treatments, treatmentIdx) => {
    // mutate in place - the rules read the state array, which shares these objects
    treatments[treatmentIdx].computedIndexDate = computeIndexDate(
      treatments[treatmentIdx],
    );
  };

  // Re-evaluates the AG11.1 rules for all groups (the lot ordering rule spans groups).
  // Only the unknown-date rule makes a cohort ineligible; date order / age errors block
  // validating the form and are reported through the warning.
  const recomputeTerminateForTreatment = (
    treatmentIdx,
    treatment,
    eligibilityDates,
    showWarning,
    allTreatments = bispecificTreatments,
  ) => {
    if (showWarning !== true) return;

    const rules = evaluateCohortDateRules(allTreatments, eligibilityDates);
    const terminateMap = {};
    const dateErrorMap = {};
    rules.forEach((r, i) => {
      terminateMap[i] = r.unknownDate;
      dateErrorMap[i] = r.dateOrderError || r.ageError;
    });

    setCohortTerminate(terminateMap);
    setCohortDateErrors(dateErrorMap);
    setEligibilityWarning(
      rules.some((r) => r.unknownDate || r.dateOrderError || r.ageError),
    );
  };

  const addAgentRow = (treatmentIdx) => {
    let updatedArray = [...bispecificTreatments];
    const currentRows = updatedArray[treatmentIdx].agentRows;
    const newRowId =
      currentRows.length > 0
        ? Math.max(...currentRows.map((r) => r.rowId)) + 1
        : 1;

    updatedArray[treatmentIdx].agentRows.push(
      JSON.parse(
        JSON.stringify({
          ...rowTemplate,
          rowId: newRowId,
        }),
      ),
    );

    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);

    setTreatmentErrors((prev) => ({
      ...prev,
      [treatmentIdx]: false,
    }));
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(
      treatmentIdx,
      treatment,
      eligibilityDates,
      false,
    );
  };

  const deleteAgentRow = (treatmentIdx, rowIdx) => {
    let updatedArray = [...bispecificTreatments];
    updatedArray[treatmentIdx].agentRows.splice(rowIdx, 1);
    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(
      treatmentIdx,
      treatment,
      eligibilityDates,
      true,
    );
  };

  const handleAgentChange = (treatmentIdx, rowIdx, agentId) => {
    let updatedArray = [...bispecificTreatments];
    const row = updatedArray[treatmentIdx].agentRows[rowIdx];

    row.agentid = agentId;
    row.agentOtherValue = "";
    row.agentError = false;

    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(
      treatmentIdx,
      treatment,
      eligibilityDates,
      false,
    );
  };

  const handleAgentOtherChange = (treatmentIdx, rowIdx, value) => {
    let updatedArray = [...bispecificTreatments];
    updatedArray[treatmentIdx].agentRows[rowIdx].agentOtherValue = value;
    updatedArray[treatmentIdx].agentRows[rowIdx].agentError = false;
    updatedArray[treatmentIdx].agentRows[rowIdx].agentOtherExcludedError =
      false;
    setBispecificTreatments(updatedArray);
  };

  const handleDateChange = (treatmentIdx, rowIdx, date) => {
    let updatedArray = [...bispecificTreatments];
    const row = updatedArray[treatmentIdx].agentRows[rowIdx];

    row.initiationDate = date;
    row.initiationDateRadio = 1;
    row.dateError = false;
    row.invalidDateError = false;

    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(
      treatmentIdx,
      treatment,
      eligibilityDates,
      true,
    );
  };

  const handleDateRadioChange = (treatmentIdx, rowIdx, value) => {
    let updatedArray = [...bispecificTreatments];
    const row = updatedArray[treatmentIdx].agentRows[rowIdx];

    row.initiationDateRadio = value;
    if (value === 99) {
      row.initiationDate = "";
    }
    row.dateError = false;
    row.invalidDateError = false;

    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(
      treatmentIdx,
      treatment,
      eligibilityDates,
      false,
    );
  };

  const getAvailableAgents = (treatmentIdx, currentRowIdx) => {
    const selectedAgentIds = bispecificTreatments[treatmentIdx].agentRows
      // .map((row, idx) =>
      //   idx !== currentRowIdx && row.agentid ? row.agentid : null
      // )
      .map((row, idx) => {
        if (idx !== currentRowIdx && row.agentid) {
          const agent = agentList.find((a) => a.agentid === row.agentid);
          const isOther = agent?.agentName?.toLowerCase().includes("other");
          return isOther ? null : row.agentid;
        }
        return null;
      })

      .filter((id) => id !== null);

    return agentList.filter(
      (agent) => !selectedAgentIds.includes(agent.agentid),
    );
  };

  const getGroupedAgents = (treatmentIdx, currentRowIdx) => {
    const availableAgents = excludeOwnBispecificAgent(
      getAvailableAgents(treatmentIdx, currentRowIdx),
      bispecificTreatments[treatmentIdx]?.cohortTreatmentID,
    );
    return groupAndSortByCategory(availableAgents);
  };

  const getAvailableAgentsForNextRow = (treatmentIdx) => {
    const selectedAgentIds = bispecificTreatments[treatmentIdx].agentRows
      .map((row) => (row.agentid ? row.agentid : null))
      .filter((id) => id !== null);

    return agentList.filter(
      (agent) => !selectedAgentIds.includes(agent.agentid),
    ).length;
  };

  const canShowAddMore = (treatmentIdx) => {
    const agentRows = bispecificTreatments[treatmentIdx].agentRows;

    if (agentRows.length === 0) return true;

    const availableAgentsCount = getAvailableAgentsForNextRow(treatmentIdx);
    return allRowsHaveAgents(agentRows) && availableAgentsCount > 0;
  };

  const ValidateForm = () => {
    let isValid = true;
    let updatedArray = [...bispecificTreatments];
    let errors = {};

    updatedArray.forEach((treatment, treatmentIdx) => {
      if (treatment.agentRows.length === 0) {
        errors[treatmentIdx] = true;
        isValid = false;
        return;
      }

      treatment.agentRows.forEach((row, rowIdx) => {
        // Hard validation: agent must be selected
        if (!row.agentid) {
          updatedArray[treatmentIdx].agentRows[rowIdx].agentError = true;
          isValid = false;
        }

        // Hard validation: if "Other" is selected, must specify
        if (
          isOtherAgentSelected(row.agentid, agentList) &&
          !row.agentOtherValue.trim()
        ) {
          updatedArray[treatmentIdx].agentRows[rowIdx].agentError = true;
          isValid = false;
        }

        // Hard validation: "Other" can't be used to re-type the agent this cohort
        // hides from the dropdown (its own bispecific counterpart)
        if (
          isOtherAgentSelected(row.agentid, agentList) &&
          isExcludedOwnBispecificAgentTyped(
            row.agentOtherValue,
            treatment.cohortTreatmentID,
          )
        ) {
          updatedArray[treatmentIdx].agentRows[rowIdx].agentError = true;
          updatedArray[treatmentIdx].agentRows[rowIdx].agentOtherExcludedError =
            true;
          isValid = false;
        }

        // Hard validation: date radio must be selected
        if (!row.initiationDateRadio) {
          updatedArray[treatmentIdx].agentRows[rowIdx].dateError = true;
          isValid = false;
        }

        // Hard validation: if radio is 1 (known date), date must be provided
        if (row.initiationDateRadio === 1 && !row.initiationDate) {
          updatedArray[treatmentIdx].agentRows[rowIdx].dateError = true;
          isValid = false;
        }
        if (row.initiationDateRadio === 1 && row.initiationDate) {
          const date = dayjs(row.initiationDate);

          if (!DateInRange(row.initiationDate, dayjs("01-01-1920"), dayjs())) {
            updatedArray[treatmentIdx].agentRows[rowIdx].invalidDateError =
              true;
            isValid = false;
          }
        }
      });
    });

    // AG11.1: date order (2) and age (3) failures cannot be submitted
    const rules = evaluateCohortDateRules(updatedArray, eligibilityDates);
    if (rules.some((r) => r.dateOrderError || r.ageError)) {
      isValid = false;
    }
    recomputeTerminateForTreatment(
      0,
      null,
      eligibilityDates,
      true,
      updatedArray,
    );

    setBispecificTreatments(updatedArray);
    setTreatmentErrors(errors);

    if (!isValid) {
      setShowTopErrMsg(true);
      setTimeout(() => setShowTopErrMsg(false), 5000);
    }

    setIsAllDataFilled(isValid);
    return isValid;
  };

  const ShowAlertBox = async () => {
    const choice = await ShowActionPopUp(
      "Alert", //title
      CommonError_Msgs.DateChangeAlert, //message
      "Ok", //Confirm button text
      true,
    );
  };

  const SavePage = async (validate) => {
    try {
      ToggleLoader(true);

      // On validate, run validation for hard errors only
      let isValid = true;
      if (validate) {
        isValid = ValidateForm();

        // If hard validation fails (missing fields, etc.), block save
        if (!isValid) {
          setShowTopErrMsg(true);
          ToggleLoader(false);
          return false;
        }
      }
      setShowTopErrMsg(false);

      const cohortRules = evaluateCohortDateRules(
        bispecificTreatments,
        eligibilityDates,
      );
      const metInclusionCriteria = calculateEligibility(cohortRules);

      setIsEligible(metInclusionCriteria);
      const payload = {
        patientid: pid,
        UserId: GetLoggedInUserID(),
        isAdmin: IsAdmin(),
        isValidated: validate,
        // Send eligibility flag to backend (like RRMM)
        metInclusionCriteria: metInclusionCriteria,
        bispecificDetails: bispecificTreatments.map((treatment, idx) => ({
          cohortTreatmentID: treatment.cohortTreatmentID,
          lotnumber: treatment.lineOfTherapy || null,
          metEligibilityCriteria: !cohortRules[idx]?.unknownDate,
          agents: treatment.agentRows.map((row) => ({
            rrmmtreatmentagentbridgeid: row.rrmmtreatmentagentbridgeid || null,
            agentid: row.agentid,
            agentOtherValue: row.agentOtherValue,
            initiationDate: row.initiationDate,
            initiationDateRadio: row.initiationDateRadio,
          })),
        })),
      };

      const saveResponse =
        await BispecificsCartTreatmentCohortService.SaveBispecificInitiationData(
          payload,
        );

      if (saveResponse?.status === HTTPResponse.OK) {
        ShowToast(ToastMessages.Save_Success, ToastMessageType.Success);
        // Reload to get updated terminatedPageId from backend
        await LoadData();
        ToggleLoader(false);

        if (saveResponse?.data?.showDateChangeAffectAlert) {
          ShowAlertBox();
        }

        if (metInclusionCriteria === true) {
          if (terminatedPageId === SideMenuItems.RRMMComboTherapy) {
            navigatePaths.nextPage = "/ManagePatients";
          } else {
            navigatePaths.nextPage = afterComboTherapyPage;
          }
        }
        return true;
      } else {
        throw new Error("Save failed");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      ToggleLoader(false);
      ShowToast(CommonError_Msgs.SAVE_ERR, ToastMessageType.Failed);
      return false;
    }
  };

  const calculateEligibility = (cohortRules) => {
    const storedData = GetLocalStorageData("CohortTreatment");
    let eligibleCohortIds = [];

    if (storedData) {
      const cohortTreatmentList = JSON.parse(storedData);

      // NDMM eligible → immediately eligible
      const hasEligibleNdmm = cohortTreatmentList.some(
        (c) => c.ndmm === true && c.isEligible === true,
      );

      if (hasEligibleNdmm) {
        return true;
      }

      // Get eligible RRMM cohort IDs
      eligibleCohortIds = cohortTreatmentList
        .filter((c) => c.isEligible === true && !c.ndmm)
        .map((c) => c.cohortTreatmentID);
    }

    // If no eligible RRMM cohorts
    if (eligibleCohortIds.length === 0) {
      return false;
    }

    // Every cohort+lot group is judged on its own - a group's termination has no
    // bearing on any other lot of the same cohort.
    const eligibleGroupIdxs = bispecificTreatments.reduce(
      (acc, t, i) =>
        eligibleCohortIds.includes(t.cohortTreatmentID) ? [...acc, i] : acc,
      [],
    );

    // If no bispecific record yet → NOT terminated
    const allEligibleTreatmentsTerminated =
      eligibleGroupIdxs.length > 0 &&
      eligibleGroupIdxs.every((i) => cohortRules[i]?.unknownDate === true);

    // Eligible if at least one treatment is active
    return !allEligibleTreatmentsTerminated;
  };

  const toggleNav = () => {
    const sidebar = document.getElementById("mySidebar");
    const main = document.getElementById("main");

    if (isSideMenuActive) {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
      setIsSideMenuActive(false);
    } else {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
      setIsSideMenuActive(true);
    }
  };

  useEffect(() => {
    const sidebar = document.getElementById("mySidebar");
    const main = document.getElementById("main");

    if (isSideMenuActive) {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
    } else {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
    }
  }, [isSideMenuActive]);

  function getCohortNameById(cohortId) {
    const cohortNames = {
      9: "TECVAYLI",
      10: "TALVEY",
      11: "ELREXFIO",
      12: "Linvoseltamab",
      13: "CARVYKTI",
      14: "ABECMA",
    };
    return cohortNames[cohortId] || `Cohort ${cohortId}`;
  }
  return (
    <>
      <section className="survey-patient-details-section">
        <div className="row">
          <PatientDetails />
          <div className="col-12 col-sm-5 col-md-5 col-lg-6 col-content-alignment">
            <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
          </div>
        </div>
      </section>

      <section className="main-survey-section">
        <div>
          <SideBarMenu activePageId={SideMenuItems.RRMMComboTherapy} />

          <button
            className={isSideMenuActive ? "openbtn" : "openbtn active"}
            onClick={toggleNav}
          >
            <i
              id="arrowNavigation"
              className="fa fa-arrow-left"
              aria-hidden="true"
            ></i>
          </button>

          <div className="col-pad-left eligibiltylocked" id="main">
            <div className="survey-section-content">
              <div className="survey-section-content-header">
                <h4>RRMM COMBO THERAPY</h4>
              </div>

              {!showTopErrMsg && pid != 0 && !isEligible && isAllDataFilled && (
                <div className="clean-error">
                  <p>{EligibilityCriteria_Msgs.IneligiblePatient}</p>
                </div>
              )}
              {showTopErrMsg && (
                <div className="clean-error">
                  <ErrorField
                    show={showTopErrMsg}
                    messageAnswer
                    message={CommonError_Msgs.Hard_Empty}
                  />
                </div>
              )}
              <div
                className="survey-question-section"
                disabled={eligibilityCriteriaLocked}
              >
                <div className="survey-question-content">
                  <div>
                    <div
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "16px",
                        lineHeight: "1.6",
                        fontWeight: "500",
                      }}
                      className="d-flex gap-1"
                    >
                      <span>S12.1.</span>
                      <span>
                        Please provide the date of treatment initiation for each
                        agent received as part of a combination therapy with a
                        Bispecific treatment. Add additional rows for each agent
                        received during that line of therapy. Please note the
                        following:
                      </span>
                    </div>
                    <ol
                      style={{
                        margin: "0",
                        paddingLeft: "50px",
                        fontSize: "16px",
                        listStyleType: "lower-alpha",
                      }}
                    >
                      <li style={{ marginBottom: "8px" }}>
                        Please list one agent per row. If the <em>"Other"</em>{" "}
                        field is used to provide an agent not within the
                        pre-specified fields, please ensure each row corresponds
                        to only one agent in the <em>"Other"</em> row.
                      </li>
                      <li style={{ marginBottom: "0" }}>
                        If a Bispecific therapy was used as bridging therapy for
                        CAR-T, this is not considered combination therapy.
                        Please abstract this as a line of CAR-T therapy only;
                        the bispecific therapy should not be abstracted as its
                        own cohort nor as a combination agent for the CAR-T
                        therapy.
                      </li>
                      <li style={{ marginBottom: "0" }}>
                        <u>
                          <b>UPDATE AS OF 9/1/2026:</b>
                        </u>{" "}
                        eMMpower allows for{" "}
                        <em>
                          Tec and Tal to be received with any combination agents
                        </em>{" "}
                        but will aim to prioritize Tec + Daratumumab and Tal +
                        Daratumumab ± Pomalidomide.
                      </li>
                    </ol>

                    {eligibilityWarning && (
                      <div style={{ marginTop: "10px" }}>
                        <ErrorField
                          show={true}
                          messageAnswer
                          message={
                            <>
                              WARNING: you have either: (i) an unknown date of
                              treatment initiation for a combination agent, (ii)
                              the earliest agent initiation date precedes the
                              patient’s date of birth, initial MM diagnosis, or
                              any reported NDMM initiation date, or (iii) a
                              combination agent initiation date for a
                              Bispecific/CAR-T therapy exceeds the start of
                              another Bispecific/CAR-T therapy. If left
                              unchanged, this patient will be considered{" "}
                              <u>
                                <i>ineligible</i>
                              </u>{" "}
                              upon validating this form. Please confirm the
                              dates of initiation for each agent and update, as
                              needed.
                            </>
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div className="question-answer">
                    {bispecificTreatments &&
                      bispecificTreatments.length > 0 && (
                        <>
                          {bispecificTreatments.map(
                            (treatment, treatmentIdx) => (
                              <div
                                key={`treatment-${treatment.cohortTreatmentID}-${treatment.lineOfTherapy || 0}`}
                                style={{ marginBottom: "30px" }}
                              >
                                <CollapsibleSectionHeader
                                  title={
                                    <>
                                      {treatment.lineOfTherapy > 0 &&
                                        `${treatment.lineOfTherapy}L – `}
                                      {treatment.cohortTreatmentName}
                                      {treatment.superScriptText && (
                                        <sup className="superscript">
                                          {treatment.superScriptText.slice(
                                            0,
                                            2,
                                          )}
                                        </sup>
                                      )}
                                      {treatment.superScriptText &&
                                        treatment.superScriptText.slice(2)}
                                    </>
                                  }
                                  isExpanded={!isCohortCollapsed(treatmentIdx)}
                                  onToggle={() =>
                                    toggleCohortCollapse(treatmentIdx)
                                  }
                                  hasError={
                                    !!treatmentErrors[treatmentIdx] ||
                                    !!cohortTerminate[treatmentIdx] ||
                                    !!cohortDateErrors[treatmentIdx] ||
                                    treatment.agentRows.some(
                                      (row) =>
                                        row.agentError ||
                                        row.dateError ||
                                        row.invalidDateError,
                                    )
                                  }
                                />
                                <Collapse in={!isCohortCollapsed(treatmentIdx)}>
                                  <div>
                                    <div className="question-bot-space">
                                      <div
                                        style={{
                                          overflowX: "auto",
                                          marginTop: "15px",
                                        }}
                                      >
                                        <table
                                          style={{
                                            border: "1px solid #333",
                                            borderCollapse: "collapse",
                                            width: "100%",
                                            minWidth: "600px",
                                          }}
                                        >
                                          <thead>
                                            <tr
                                              style={{
                                                backgroundColor: "#f5f5f5",
                                              }}
                                            >
                                              <th
                                                style={{
                                                  border: "1px solid #333",
                                                  padding: "10px",
                                                  textAlign: "center",
                                                  width: "40%",
                                                }}
                                              >
                                                Combination agent used
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid #333",
                                                  padding: "10px",
                                                  textAlign: "center",
                                                  width: "40%",
                                                }}
                                              >
                                                Date of Treatment Initiation
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid #333",
                                                  padding: "10px",
                                                  textAlign: "center",
                                                  width: "10%",
                                                }}
                                              >
                                                Action
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {treatment.agentRows.map(
                                              (row, rowIdx) => (
                                                <tr
                                                  key={`row-${treatmentIdx}-${row.rowId}`}
                                                >
                                                  <td
                                                    style={{
                                                      border: "1px solid #333",
                                                      padding: "10px",
                                                      verticalAlign: "top",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        gap: "8px",
                                                        alignItems:
                                                          "flex-start",
                                                        flexWrap: "wrap",
                                                      }}
                                                    >
                                                      <select
                                                        value={
                                                          row.agentid || ""
                                                        }
                                                        onChange={(e) =>
                                                          handleAgentChange(
                                                            treatmentIdx,
                                                            rowIdx,
                                                            e.target.value
                                                              ? Number(
                                                                  e.target
                                                                    .value,
                                                                )
                                                              : null,
                                                          )
                                                        }
                                                        style={{
                                                          minWidth: "100%",
                                                          padding: "8px",
                                                          borderRadius: "4px",
                                                          border: row.agentError
                                                            ? "2px solid #d32f2f"
                                                            : "1px solid #ccc",
                                                          fontSize: "13px",
                                                        }}
                                                      >
                                                        <option value="">
                                                          -- Select Agent --
                                                        </option>
                                                        {Object.entries(
                                                          getGroupedAgents(
                                                            treatmentIdx,
                                                            rowIdx,
                                                          ),
                                                        ).map(
                                                          ([
                                                            category,
                                                            agents,
                                                          ]) => (
                                                            <optgroup
                                                              key={category}
                                                              label={category}
                                                            >
                                                              {sortAgents(
                                                                agents,
                                                              ).map((agent) => (
                                                                <option
                                                                  key={
                                                                    agent.agentid
                                                                  }
                                                                  value={
                                                                    agent.agentid
                                                                  }
                                                                >
                                                                  {
                                                                    agent.agentName
                                                                  }
                                                                </option>
                                                              ))}
                                                            </optgroup>
                                                          ),
                                                        )}
                                                      </select>

                                                      {isOtherAgentSelected(
                                                        row.agentid,
                                                        agentList,
                                                      ) && (
                                                        <input
                                                          type="text"
                                                          value={
                                                            row.agentOtherValue
                                                          }
                                                          onChange={(e) =>
                                                            handleAgentOtherChange(
                                                              treatmentIdx,
                                                              rowIdx,
                                                              e.target.value,
                                                            )
                                                          }
                                                          placeholder="please specify"
                                                          style={{
                                                            minWidth: "200px",
                                                            padding: "6px 8px",
                                                            borderRadius: "4px",
                                                            border:
                                                              row.agentError
                                                                ? "2px solid #d32f2f"
                                                                : "1px solid #ccc",
                                                            fontSize: "12px",
                                                            flex: "1",
                                                          }}
                                                        />
                                                      )}
                                                    </div>

                                                <ErrorField
                                                  show={row.agentError}
                                                  message={
                                                    row.agentOtherExcludedError
                                                      ? `${getExcludedOwnBispecificAgentName(
                                                          treatment.cohortTreatmentID,
                                                        )} cannot be entered as a combination agent for this cohort.`
                                                      : "Please choose one of the provided options"
                                                  }
                                                  style={{ paddingLeft: "0" }}
                                                />
                                              </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid #333",
                                                      padding: "10px",
                                                      verticalAlign: "top",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "8px",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          gap: "8px",
                                                          alignItems: "center",
                                                          flexWrap: "wrap",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          name={`date-${treatmentIdx}-${rowIdx}`}
                                                          value="1"
                                                          checked={
                                                            row.initiationDateRadio ===
                                                            1
                                                          }
                                                          onChange={() =>
                                                            handleDateRadioChange(
                                                              treatmentIdx,
                                                              rowIdx,
                                                              1,
                                                            )
                                                          }
                                                          style={{
                                                            marginRight: "0px",
                                                          }}
                                                        />
                                                        <DatePickerComponent
                                                          ctrlId={`date-${treatmentIdx}-${rowIdx}`}
                                                          date={
                                                            row.initiationDate
                                                          }
                                                          HandleDateChange={(
                                                            name,
                                                            date,
                                                          ) =>
                                                            handleDateChange(
                                                              treatmentIdx,
                                                              rowIdx,
                                                              date,
                                                            )
                                                          }
                                                          minDate={dayjs(
                                                            "01-01-1920",
                                                          )}
                                                          maxDate={dayjs()}
                                                          locale="en"
                                                        />
                                                      </div>

                                                      <label
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          fontSize: "12px",
                                                          margin: "0px",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          name={`date-${treatmentIdx}-${rowIdx}`}
                                                          value="99"
                                                          checked={
                                                            row.initiationDateRadio ===
                                                            99
                                                          }
                                                          onChange={() =>
                                                            handleDateRadioChange(
                                                              treatmentIdx,
                                                              rowIdx,
                                                              99,
                                                            )
                                                          }
                                                          style={{
                                                            marginRight: "6px",
                                                          }}
                                                        />
                                                        Date of treatment
                                                        initiation unknown
                                                      </label>
                                                    </div>
                                                    <ErrorField
                                                      show={row.dateError}
                                                      message={
                                                        "Please enter the date of treatment initiation (if known) or select unknown otherwise"
                                                      }
                                                    />

                                                    <ErrorField
                                                      show={
                                                        row.invalidDateError
                                                      }
                                                      message={
                                                        "Invalid Date. Please enter a valid date and ensure that the date is not in the future."
                                                      }
                                                    />
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid #333",
                                                      padding: "10px",
                                                      textAlign: "center",
                                                      verticalAlign: "middle",
                                                    }}
                                                  >
                                                    <img
                                                      src="./Assets/images/Icon-material-delete.png"
                                                      alt="Delete"
                                                      width="18px"
                                                      style={{
                                                        cursor: "pointer",
                                                      }}
                                                      onClick={() =>
                                                        deleteAgentRow(
                                                          treatmentIdx,
                                                          rowIdx,
                                                        )
                                                      }
                                                    />
                                                  </td>
                                                </tr>
                                              ),
                                            )}

                                            {canShowAddMore(treatmentIdx) && (
                                              <tr>
                                                <td
                                                  colSpan="3"
                                                  style={{
                                                    border: "1px solid #333",
                                                    padding: "10px",
                                                    textAlign: "left",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      cursor: "pointer",
                                                    }}
                                                  >
                                                    <img
                                                      src="./Assets/images/plus.png"
                                                      alt="Add"
                                                      width="18px"
                                                      style={{
                                                        marginRight: "6px",
                                                      }}
                                                    />
                                                    <a
                                                      onClick={() =>
                                                        addAgentRow(
                                                          treatmentIdx,
                                                        )
                                                      }
                                                      style={{
                                                        color: "#2196F3",
                                                        textDecoration: "none",
                                                        fontSize: "13px",
                                                        cursor: "pointer",
                                                      }}
                                                    >
                                                      Click to add another agent
                                                    </a>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>

                                      {treatmentErrors[treatmentIdx] && (
                                        <div>
                                          <ErrorField
                                            show={true}
                                            message={`Please fill data for ${treatment.cohortTreatmentName}`}
                                          />
                                        </div>
                                      )}

                                      {cohortTerminate[treatmentIdx] && (
                                        <div style={{ marginTop: "8px" }}>
                                          <ErrorField
                                            show={true}
                                            message={`This patient does not meet the eligibility criteria for this treatment cohort.`}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Collapse>
                              </div>
                            ),
                          )}
                        </>
                      )}
                  </div>
                </div>
              </div>
              <div className="survey-section-content-foot">
                <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BispecificInitiation;
